import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Tabs, message } from 'antd';
import { HeaderTitleComponent } from '../../../../Components/ContentComponent/HeaderTitleComponent';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ContentComponent } from '../../../../Components/ContentComponent/ContentComponent';
import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { validationSchema } from '../NewFreeRoundsPromotion/validationSchema';
import ConfigurationTab from '../NewFreeRoundsPromotion/ConfigurationTab';
import PlayersTab from '../NewFreeRoundsPromotion/PlayersTab';
import GamesTab from '../NewFreeRoundsPromotion/GamesTab';
import VisualCustomizationTab from '../NewFreeRoundsPromotion/VisualCustomizationTab';
import {
    cloneCampaign,
    editFreeRoundsCampaign,
    resetNewPromotion,
} from '../../../../redux/actions/backoffice/promotions-actions';
import { useDispatch, useSelector } from 'react-redux';
import {
    campaignHasBeenCreatedData,
    getClonedCampaignCsvData,
    getClonedCampaignData,
} from '../../../../redux/selectors/backoffice/propmotions-selectors';
import Papa from 'papaparse';
import dayjs from 'dayjs';
import { currencyUtils } from '../../../../utils';
import { formatPromotionTotalCost } from '../../promotionUtils';
import { parseCampaignDateTime } from '../../promotionDateTime';
import { canManagePromotionCampaign } from '../../promotionUtils';

const { TabPane } = Tabs;

interface IFreeRoundsCampaignProps {
    id: number | null;
    campaign_name: string;
    owner?: string | null;
    externalCampaignId: string | null;
    timezone: 'Etc/UTC';
    start_at: string;
    end_at: string;
    number_of_players: number;
    valid_for: number | null;
    strategy: string;
    start_event: string;
    players: string[];
    currency: string;
    subunit: number;
    price: number | null;
    number_of_features: number | null;
    gameIds: string[];
    allowPostExpirationCompletion: boolean;
    rulesUrl: string;
    promotionSettings?: {
        expiresInEnabled?: boolean;
        optOutConfirmationPopupEnabled?: boolean;
        optOutEnabled?: boolean;
        valuedAtEnabled?: boolean;
        youWonEnabled?: boolean;
        playLaterEnabled?: boolean;
    };
}

const getInitialValues = (): IFreeRoundsCampaignProps => {
    return {
        id: null,
        campaign_name: '',
        owner: null,
        externalCampaignId: null,
        timezone: 'Etc/UTC',
        start_at: '',
        end_at: '',
        number_of_players: 1000,
        valid_for: null,
        strategy: 'FIRST_N_PLAYERS',
        start_event: 'ACCEPT',
        players: [],
        currency: 'USD',
        subunit: 100,
        price: 0,
        number_of_features: null,
        gameIds: [],
        allowPostExpirationCompletion: false,
        rulesUrl: '',
        promotionSettings: {
            expiresInEnabled: false,
            optOutConfirmationPopupEnabled: false,
            optOutEnabled: false,
            valuedAtEnabled: true,
            youWonEnabled: true,
            playLaterEnabled: true,
        },
    };
};

const EditFreeRoundsCampaign = () => {
    const { t } = useTranslation();
    const params = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const campaignHasBeenCreated = useSelector(campaignHasBeenCreatedData);
    const [externalIdModalVisible, setExternalIdModalVisible] = useState(false);
    const [discardConfirmVisible, setDiscardConfirmVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [formValues, setFormValues] = useState<any>(null);
    const [statusCheckComplete, setStatusCheckComplete] = useState(false);
    const [initialPlayers, setInitialPlayers] = useState<string[]>([]);
    const initialValues = useMemo(() => getInitialValues(), []);
    const campaignData: any = useSelector(getClonedCampaignData);
    const campaignDataCsv: any = useSelector(getClonedCampaignCsvData);

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(t),
        onSubmit: (values) => {
            setFormValues(values);
            if (formik.values.externalCampaignId) {
                setExternalIdModalVisible(true);
            } else setConfirmVisible(true);
        },
    });
    const formikRef = useRef(formik);

    const handleCSVParse = useCallback((csvData: string) => {
        Papa.parse(csvData, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const playerIds = results.data
                    .map((row: any) => String(Object.values(row)[0] || '').trim())
                    .filter(Boolean);
                const uniquePlayerIds = Array.from(new Set(playerIds));
                const currentPlayers = formikRef.current.values.players || [];
                const updatedPlayers = Array.from(new Set([...currentPlayers, ...uniquePlayerIds]));

                formikRef.current.setFieldValue('players', updatedPlayers);
                setInitialPlayers(uniquePlayerIds);

                formikRef.current.validateField('players');
            },
        });
    }, []);

    useEffect(() => {
        dispatch(cloneCampaign(params.campaignId));
    }, [dispatch, params.campaignId]);

    useEffect(() => {
        formikRef.current = formik;
    }, [formik]);

    useEffect(() => {
        if (campaignData) {
            const newValues: any = {
                id: campaignData.id,
                campaign_name: campaignData.name,
                start_at: parseCampaignDateTime(campaignData.startDate, campaignData.timezone),
                end_at: parseCampaignDateTime(campaignData.endDate, campaignData.timezone),
                owner: campaignData.owner || null,
                externalCampaignId: campaignData.externalCampaignId || null,
                timezone: campaignData.timezone,
                number_of_players: campaignData.eligiblePlayersNumber || null,
                valid_for: campaignData.validForDays || null,
                strategy: campaignData.strategy,
                currency: campaignData.currencyCode,
                subunit:
                    currencyUtils.getCurrencyDataByCode(campaignData.currencyCode).subunit || 100,
                price: campaignData.bet,
                number_of_features: campaignData.betNumber,
                gameIds: campaignData.games.map((game: any) => game.id) || [],
                start_event: campaignData.startEvent || null,
                allowPostExpirationCompletion: campaignData.allowPostExpirationCompletion || null,
                rulesUrl: campaignData.rulesUrl || null,
                promotionSettings: campaignData.promotionSettings || {
                    expiresInEnabled: false,
                    optOutConfirmationPopupEnabled: false,
                    optOutEnabled: false,
                    valuedAtEnabled: true,
                    youWonEnabled: true,
                    playLaterEnabled: true,
                },
            };

            if (JSON.stringify(formikRef.current.values) !== JSON.stringify(newValues)) {
                formikRef.current.setValues(newValues);
            }
        }
    }, [campaignData]);

    useEffect(() => {
        if (campaignDataCsv) {
            handleCSVParse(campaignDataCsv);
        }
    }, [campaignDataCsv, handleCSVParse]);

    useEffect(() => {
        if (campaignHasBeenCreated) {
            navigate(`/casino/free-rounds-promotion/${params.campaignId}`);
        }

        return () => {
            dispatch(resetNewPromotion());
        };
    }, [navigate, dispatch, campaignHasBeenCreated, params.campaignId]);

    useEffect(() => {
        if (campaignData && !statusCheckComplete) {
            if (!canManagePromotionCampaign(campaignData)) {
                message.error('You do not have permission to edit this campaign.');
                navigate(`/casino/free-rounds-promotion/${params.campaignId}`);
                return;
            }

            if (campaignData.status !== 'UPCOMING') {
                message.error(`Cannot edit campaign. Only campaigns with 'UPCOMING' status can be edited. Current status: ${campaignData.status}`);
                navigate(`/casino/free-rounds-promotion/${params.campaignId}`);
                return;
            }
            setStatusCheckComplete(true);
        }
    }, [campaignData, statusCheckComplete, navigate, params.campaignId]);

    const handleConfirmSubmit = () => {
        dispatch(editFreeRoundsCampaign({ ...formValues, originalPlayers: initialPlayers }));
        setConfirmVisible(false);
    };

    const handleDiscard = () => {
        setDiscardConfirmVisible(true);
    };

    const handleConfirmExternalID = () => {
        setConfirmVisible(true);
        setExternalIdModalVisible(false);
    };

    const confirmDiscard = () => {
        formik.resetForm();
        navigate(`/casino/free-rounds-promotion/${campaignData.id}`);
        setDiscardConfirmVisible(false);
    };

    const costPerPlayer = useMemo(() => {
        return formik.values.price && formik.values.number_of_features && formik.values.subunit
            ? formik.values.number_of_features * (formik.values.price / formik.values.subunit)
            : 0;
    }, [formik.values.price, formik.values.number_of_features, formik.values.subunit]);

    const totalPlayers = useMemo(() => {
        if (formik.values.strategy === 'FIRST_N_PLAYERS' && formik.values.number_of_players) {
            return formik.values.number_of_players;
        }
        if (formik.values.strategy === 'PLAYER_LIST' && formik.values.players?.length > 0) {
            return formik.values.players.length;
        }
        return 0;
    }, [formik.values.strategy, formik.values.number_of_players, formik.values.players]);

    const calculatedTotalCost = useMemo(
        () => costPerPlayer * totalPlayers,
        [costPerPlayer, totalPlayers],
    );

    const tabHasErrors = (fields: Array<keyof IFreeRoundsCampaignProps>) =>
        fields.some((field) => formik.errors[field] && formik.touched[field]);

    const tabsFields: any = {
        configuration: [
            'campaign_name',
            'start_at',
            'end_at',
            'externalCampaignId',
            'timezone',
            'valid_for',
            'start_event',
            'allowPostExpirationCompletion',
            'rulesUrl',
        ],
        players: ['number_of_players', 'strategy', 'players'],
        games: ['currency', 'price', 'number_of_features', 'gameIds'],
    };

    // Don't render the form if status check is not complete or campaign is not UPCOMING
    if (!campaignData || !statusCheckComplete) {
        return (
            <ContentComponent
                header={
                    <HeaderTitleComponent
                        title={'Free Rounds'}
                        customBreadcrumbs={
                            <Link to={`/casino/free-rounds-promotions`}>{'Promotions'}</Link>
                        }
                    />
                }
                innerContent={
                    <div className="p-6 bg-white rounded-lg">
                        <div className="flex justify-center items-center h-64">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading campaign data...</p>
                            </div>
                        </div>
                    </div>
                }
            />
        );
    }

    return (
        <ContentComponent
            header={
                <div className="flex justify-between">
                    <HeaderTitleComponent
                        title={'Free Rounds'}
                        customBreadcrumbs={
                            <Link to={`/casino/free-rounds-promotions`}>{'Promotions'}</Link>
                        }
                    />
                    <div className="flex items-end">
                        <Button size="large" shape="round" onClick={handleDiscard}>
                            Discard Changes
                        </Button>
                        <Button
                            danger
                            className="ml-2"
                            size="large"
                            shape="round"
                            onClick={formik.submitForm}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            }
            innerContent={
                <div className="p-6 bg-white rounded-lg">
                    <Modal
                        title="Are you sure you want to discard all changes?"
                        open={discardConfirmVisible}
                        onOk={confirmDiscard}
                        onCancel={() => setDiscardConfirmVisible(false)}
                    />
                    <Modal
                        destroyOnClose
                        title="Confirm External Campaign ID"
                        open={externalIdModalVisible}
                        footer={null}
                        onCancel={() => setExternalIdModalVisible(false)}
                        className="p-6 rounded-lg"
                    >
                        <p className="mb-4">
                            You entered an External Campaign ID, this field will be propagated in
                            wallet operations as per integration documentation. Are you sure?
                        </p>
                        <div className="flex justify-end space-x-4">
                            <Button
                                size="large"
                                shape="round"
                                onClick={() => setExternalIdModalVisible(false)}
                            >
                                {t('no')}
                            </Button>
                            <Button
                                size="large"
                                shape="round"
                                type="primary"
                                onClick={handleConfirmExternalID}
                            >
                                {t('yes')}
                            </Button>
                        </div>
                    </Modal>
                    <Modal
                        title="Confirm Promotion Submission"
                        open={confirmVisible}
                        onOk={handleConfirmSubmit}
                        onCancel={() => setConfirmVisible(false)}
                    >
                        {formValues && (
                            <div className="space-y-4 p-4  rounded-lg border-dashed">
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">
                                        Campaign Name:
                                    </span>
                                    <span className="text-gray-900">
                                        {formValues.campaign_name}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">Timezone:</span>
                                    <span className="text-gray-900">{formValues.timezone}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">Start Date:</span>
                                    <span className="text-gray-900">
                                        {dayjs(formValues.start_at).format('HH:mm  MMMM D, YYYY')}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">End Date:</span>
                                    <span className="text-gray-900">
                                        {dayjs(formValues.end_at).format('HH:mm MMMM D, YYYY')}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">
                                        Number of Players:
                                    </span>
                                    <span className="text-gray-900">{totalPlayers}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700 font-bold">Total cost:</span>
                                    <span className="text-gray-900 font-bold">
                                        {formatPromotionTotalCost(
                                            calculatedTotalCost,
                                            formValues?.subunit,
                                        )}{' '}
                                        {formValues.currency}
                                    </span>
                                </div>
                            </div>
                        )}
                    </Modal>
                    <FormikProvider value={formik}>
                        <Form onSubmit={formik.handleSubmit}>
                            <Tabs defaultActiveKey="1">
                                <TabPane
                                    tab={`Configuration ${tabHasErrors(tabsFields.configuration) ? '!' : ''}`}
                                    key="1"
                                >
                                    <ConfigurationTab formik={formik} />
                                </TabPane>
                                <TabPane
                                    tab="Visual Customization"
                                    key="2"
                                >
                                    <VisualCustomizationTab formik={formik} />
                                </TabPane>
                                <TabPane
                                    tab={`Players ${tabHasErrors(tabsFields.players) ? '!' : ''}`}
                                    key="3"
                                >
                                    <PlayersTab formik={formik} />
                                </TabPane>
                                <TabPane
                                    tab={`Games ${tabHasErrors(tabsFields.games) ? '!' : ''}`}
                                    key="4"
                                >
                                    <GamesTab formik={formik} />
                                </TabPane>
                            </Tabs>
                        </Form>
                    </FormikProvider>
                </div>
            }
        />
    );
};

export default EditFreeRoundsCampaign;
